"use client";

/**
 * not-found.tsx — global 404 page.
 *
 * Triggered by Next.js for any unmatched route. Renders bilingual content via
 * react-i18next so the page matches the user's selected language at runtime.
 * For vendor users the primary CTA points at /vendor/overview; everyone else
 * gets a generic home link.
 *
 * Design tokens: WEB_DESIGN.md (surface bg, gradient-primary CTA, font-headline).
 */

import Link from "next/link";
import { useTranslation } from "react-i18next";

export default function NotFound() {
  const { t } = useTranslation();

  return (
    // Semantic <main> lives in the root layout to avoid nested <main> elements.
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-surface px-6 py-24 text-center">
      <div className="max-w-xl">
        <span
          className="material-symbols-outlined text-[72px] text-primary/60"
          aria-hidden
        >
          travel_explore
        </span>
        <h1 className="mt-6 font-headline text-headline-lg text-on-surface">
          {t("web.notFound.title", "Page not found")}
        </h1>
        <p className="mt-4 text-lg text-on-surface-variant">
          {t(
            "web.notFound.description",
            "The page you requested does not exist. You can head back to the overview or find another way around.",
          )}
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            href="/vendor/overview"
            className="inline-flex items-center gap-2 rounded-full gradient-primary px-6 py-3 text-sm font-semibold text-on-primary shadow-glow transition-all hover:shadow-glow-lg"
          >
            <span
              className="material-symbols-outlined rtl-flip text-[18px]"
              aria-hidden
            >
              arrow_back
            </span>
            {t("web.notFound.backToOverview", "Back to overview")}
          </Link>
          <Link
            href="/"
            className="inline-flex items-center gap-2 rounded-full bg-surface-container-low px-6 py-3 text-sm font-semibold text-on-surface transition-colors hover:bg-surface-container"
          >
            {t("web.notFound.backToHome", "Back to home")}
          </Link>
        </div>
      </div>
    </div>
  );
}
